import React, { useMemo, useState } from 'react';

/**
 * Reusable component to display player categories in a table format
 * Used in both Dashboard and Session Simulation pages
 * 
 * @param {Array} players - Array of player objects
 * @param {Object} playerCategories - Categories object from categorizePlayersForSession
 *   Structure: { mixed: {...}, levels_male: {...}, levels_female: {...} }
 *   Each containing: { categories: {playerId: 'A'}, categoryCount, categoryRanges, stats, method, etc. }
 * @param {Object} startingElos - Optional object mapping playerId to starting ELOs
 *   Used when categories were calculated from starting ELOs (e.g., in simulations)
 *   Structure: { playerId: { mixed_elo_rating: 1200, levels_elo_rating: 1200 } }
 * @param {boolean} showMixed - Whether to show mixed doubles tab
 * @param {boolean} showLevels - Whether to show levels tabs
 */
const PlayerCategoriesTable = ({ 
  players = [], 
  playerCategories = null,
  startingElos = null,
  showMixed = true,
  showLevels = true,
}) => {
  const [rankingTab, setRankingTab] = useState(showMixed ? 'mixed' : 'male');
  const [rankingVisibility, setRankingVisibility] = useState({
    mixed: false,
    male: false,
    female: false,
  });

  // Get gender text color for player names
  const getGenderTextColor = (gender) => {
    if (gender === 'male') return 'text-blue-600';
    if (gender === 'female') return 'text-purple-600';
    return 'text-gray-700';
  };

  // Format ELO value
  const formatEloValue = (elo) => {
    if (elo === null || elo === undefined) return '—';
    return Math.round(elo);
  };

  // Toggle visibility of uncategorised players
  const toggleRankingVisibility = (key) => {
    setRankingVisibility(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Prepare ranking data for mixed doubles
  const mixedRanking = useMemo(() => {
    if (!players || players.length === 0) {
      return { categorised: [], uncategorised: [] };
    }

    const rows = players
      .map(p => {
        // Use starting ELO if available (for simulations), otherwise use current ELO
        const mixedElo = startingElos?.[p.id]?.mixed_elo_rating ?? p.mixed_elo_rating;
        if (mixedElo === null || mixedElo === undefined) return null;
        
        const category = playerCategories?.mixed?.categories?.[p.id] || null;
        return {
          id: p.id,
          name: `${p.first_name || ''} ${p.last_name || ''}`.trim() || `Player ${p.id}`,
          gender: p.gender,
          mixedElo: mixedElo,
          mixedCategory: category,
          gamesPlayed: p.games_played || 0,
        };
      })
      .filter(Boolean)
      .sort((a, b) => (b.mixedElo || 0) - (a.mixedElo || 0));

    return {
      categorised: rows.filter(r => r.mixedCategory),
      uncategorised: rows.filter(r => !r.mixedCategory),
    };
  }, [players, playerCategories, startingElos]);

  // Prepare ranking data for male levels
  const maleRanking = useMemo(() => {
    if (!players || players.length === 0) {
      return { categorised: [], uncategorised: [] };
    }

    const rows = players
      .filter(p => p.gender === 'male')
      .map(p => {
        // Use starting ELO if available (for simulations), otherwise use current ELO
        const levelsElo = startingElos?.[p.id]?.levels_elo_rating ?? p.levels_elo_rating;
        if (levelsElo === null || levelsElo === undefined) return null;
        
        const category = playerCategories?.levels_male?.categories?.[p.id] || null;
        return {
          id: p.id,
          name: `${p.first_name || ''} ${p.last_name || ''}`.trim() || `Player ${p.id}`,
          gender: p.gender,
          levelsElo: levelsElo,
          levelsCategory: category,
          gamesPlayed: p.games_played || 0,
        };
      })
      .filter(Boolean)
      .sort((a, b) => (b.levelsElo || 0) - (a.levelsElo || 0));

    return {
      categorised: rows.filter(r => r.levelsCategory),
      uncategorised: rows.filter(r => !r.levelsCategory),
    };
  }, [players, playerCategories, startingElos]);

  // Prepare ranking data for female levels
  const femaleRanking = useMemo(() => {
    if (!players || players.length === 0) {
      return { categorised: [], uncategorised: [] };
    }

    const rows = players
      .filter(p => p.gender === 'female')
      .map(p => {
        // Use starting ELO if available (for simulations), otherwise use current ELO
        const levelsElo = startingElos?.[p.id]?.levels_elo_rating ?? p.levels_elo_rating;
        if (levelsElo === null || levelsElo === undefined) return null;
        
        const category = playerCategories?.levels_female?.categories?.[p.id] || null;
        return {
          id: p.id,
          name: `${p.first_name || ''} ${p.last_name || ''}`.trim() || `Player ${p.id}`,
          gender: p.gender,
          levelsElo: levelsElo,
          levelsCategory: category,
          gamesPlayed: p.games_played || 0,
        };
      })
      .filter(Boolean)
      .sort((a, b) => (b.levelsElo || 0) - (a.levelsElo || 0));

    return {
      categorised: rows.filter(r => r.levelsCategory),
      uncategorised: rows.filter(r => !r.levelsCategory),
    };
  }, [players, playerCategories, startingElos]);

  // Define columns for mixed
  const mixColumns = [
    {
      key: 'mixedElo',
      label: 'Mixed ELO',
      render: (row) => formatEloValue(row.mixedElo),
    },
    {
      key: 'mixedCategory',
      label: 'Mixed Cat',
      render: (row) => row.mixedCategory || '—',
    },
    {
      key: 'gamesPlayed',
      label: 'Games',
      render: (row) => row.gamesPlayed ?? 0,
    },
  ];

  // Define columns for levels
  const levelColumns = [
    {
      key: 'levelsElo',
      label: 'Levels ELO',
      render: (row) => formatEloValue(row.levelsElo),
    },
    {
      key: 'levelsCategory',
      label: 'Levels Cat',
      render: (row) => row.levelsCategory || '—',
    },
    {
      key: 'gamesPlayed',
      label: 'Games',
      render: (row) => row.gamesPlayed ?? 0,
    },
  ];

  // Render category chips
  const renderCategoryChips = (meta) => {
    if (!meta || meta.categoryCount === 0) return null;
    
    const categories = ['A', 'B', 'C', 'D'].slice(0, meta.categoryCount);
    return (
      <div className="flex gap-1.5 mt-2">
        {categories.map((cat) => (
          <span
            key={cat}
            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
          >
            {cat}
          </span>
        ))}
      </div>
    );
  };

  // Get section status text
  const getSectionStatusText = (meta, totalPlayers) => {
    if (!meta) return '';
    if (meta.categoryCount > 0) {
      const methodLabel = meta.method ? ` · method: ${meta.method.replace(/_/g, ' ')}` : '';
      const playerLabel = totalPlayers ? ` · ${totalPlayers} players` : '';
      return `${meta.categoryCount} categories${playerLabel}${methodLabel}`;
    }
    switch (meta.disabledReason) {
      case 'not_enough_players':
        return 'Not enough players to build categories.';
      case 'spread_too_small':
        return 'ELO spread was too small to create distinct tiers.';
      case 'not_calculated':
        return 'Awaiting category calculation.';
      default:
        return 'No categories calculated yet.';
    }
  };

  // Build ranking sections
  const rankingSections = useMemo(() => {
    const sections = [];
    
    if (showMixed) {
      sections.push({
        key: 'mixed',
        title: 'Mixed Doubles',
        description: 'Categories based on mixed doubles ELO',
        data: mixedRanking,
        meta: playerCategories?.mixed || null,
        columns: mixColumns,
        categoryKey: 'mixedCategory',
        emptyLabel: 'No players available.',
      });
    }

    if (showLevels) {
      sections.push({
        key: 'male',
        title: "Men's Levels",
        description: 'Categories based on levels (same-gender) ELO',
        data: maleRanking,
        meta: playerCategories?.levels_male || null,
        columns: levelColumns,
        categoryKey: 'levelsCategory',
        emptyLabel: 'No male players available.',
      });

      sections.push({
        key: 'female',
        title: "Women's Levels",
        description: 'Categories based on levels (same-gender) ELO',
        data: femaleRanking,
        meta: playerCategories?.levels_female || null,
        columns: levelColumns,
        categoryKey: 'levelsCategory',
        emptyLabel: 'No female players available.',
      });
    }

    return sections;
  }, [showMixed, showLevels, mixedRanking, maleRanking, femaleRanking, playerCategories, mixColumns, levelColumns]);

  const rankingSectionsMap = useMemo(() => {
    const map = {};
    rankingSections.forEach(section => {
      map[section.key] = section;
    });
    return map;
  }, [rankingSections]);

  const rankingTabOptions = useMemo(() => {
    const options = [];
    if (showMixed) options.push({ key: 'mixed', label: 'Mixed Doubles' });
    if (showLevels) {
      options.push({ key: 'male', label: "Men's Levels" });
      options.push({ key: 'female', label: "Women's Levels" });
    }
    return options;
  }, [showMixed, showLevels]);

  const currentRankingSection = rankingSectionsMap[rankingTab] || rankingSections[0] || null;

  // Render ranking section
  const renderRankingSection = ({
    key,
    title,
    description,
    data,
    meta,
    columns,
    categoryKey,
    emptyLabel,
  }) => {
    const dataset = data || { categorised: [], uncategorised: [] };
    const showAllRows = rankingVisibility[key];
    const hiddenCount = dataset.uncategorised.length;
    const rows = showAllRows
      ? [...dataset.categorised, ...dataset.uncategorised]
      : dataset.categorised;
    const statusText = getSectionStatusText(meta, dataset.categorised.length + dataset.uncategorised.length);
    const hasRows = rows.length > 0;

    return (
      <div key={key} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <h4 className="text-base font-semibold text-gray-900">{title}</h4>
            <p className="text-sm text-gray-600">{description}</p>
            <p className="text-xs text-gray-500 mt-1">{statusText}</p>
            {renderCategoryChips(meta)}
          </div>
          {hiddenCount > 0 && (
            <button
              onClick={() => toggleRankingVisibility(key)}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              {showAllRows ? 'Hide uncategorised players' : `Show ${hiddenCount} uncategorised`}
            </button>
          )}
        </div>
        <div className="mt-4">
          {!hasRows ? (
            <div className="p-4 border border-dashed border-gray-200 rounded-lg text-sm text-gray-600">
              {dataset.categorised.length === 0 && hiddenCount > 0 && !showAllRows
                ? 'No players with a category yet. Show uncategorised players to review everyone.'
                : emptyLabel}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">#</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Player</th>
                    {columns.map((col) => (
                      <th key={col.key} className="px-3 py-2 text-left text-xs font-semibold text-gray-500">
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((row, index) => {
                    const categoryValue = row[categoryKey] || '--';
                    const isCategorised = categoryValue !== '--';
                    const prevRow = rows[index - 1];
                    const prevCategory = prevRow ? prevRow[categoryKey] : null;
                    const showDivider = isCategorised && categoryValue !== prevCategory;
                    return (
                      <tr
                        key={`${row.id}-${categoryKey}-${index}`}
                        className={`${!isCategorised ? 'opacity-70' : ''} ${
                          showDivider ? 'border-t-2 border-gray-200' : ''
                        }`}
                      >
                        <td className="px-3 py-2 text-sm text-gray-500">{index + 1}</td>
                        <td className={`px-3 py-2 font-medium ${getGenderTextColor(row.gender)}`}>
                          {row.name}
                        </td>
                        {columns.map((col) => (
                          <td key={col.key} className="px-3 py-2 text-sm text-gray-700">
                            {col.render(row)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Tab Navigation */}
      {rankingTabOptions.length > 1 && (
        <div className="flex gap-2 border-b border-gray-200">
          {rankingTabOptions.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setRankingTab(tab.key)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                rankingTab === tab.key
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Current Section */}
      {currentRankingSection && renderRankingSection(currentRankingSection)}
    </div>
  );
};

export default PlayerCategoriesTable;
